// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React from "react";
import i18n from "i18next";
import { DeleteOutlined, EyeOutlined, MoreOutlined } from "@ant-design/icons";
import TableButton from "../../../components/TableButton";
import { showConfirm } from "../../../components/ConfirmModal";
import { navigate } from "@reach/router";
import { ColumnsType } from "antd/lib/table";
import {
  UserInfoResponseDTO,
  BranchListDto
} from "../../../../api/backend-openapi";
import { requestDeleteUser } from "../../../../state/userManagement/UserManagementEffects";
import { alignRight, orderAscend } from "../../../../utils/Constant";
import { Button, Popover, Tag } from "antd";
import { NewsThemeStore } from "../../../../state/newsTheme/NewsThemeStore";
import { tooltipDateRender } from "../../../components/CustomTooltip";
import UserStatusTag from "../details/UserStatusTag";
import UserProfileTag from "../details/UserProfileTag";

const popoverContent = (record: UserInfoResponseDTO, canDelete: boolean) => (
  <div className="d-inline-flex">
    <TableButton
      icon={<EyeOutlined />}
      tooltipTitle={i18n.t("users.table.tooltip.view")}
      onClick={() => {
        navigate(`/users/${record.id}`);
      }}
      buttonClassName="btn-primary mr-1 btn-list"
    />
    {canDelete && (
      <TableButton
        icon={<DeleteOutlined />}
        buttonClassName="btn-danger btn-list"
        onClick={() =>
          showConfirm(
            i18n.t("users.table.confirmation.delete", {
              user: record.email
            }),
            () => {
              requestDeleteUser(record);
            }
          )
        }
        tooltipTitle={i18n.t("users.table.tooltip.delete")}
      />
    )}
  </div>
);

export const columns = (
  canDelete: boolean,
  branchList: BranchListDto
): ColumnsType<UserInfoResponseDTO> => [
  {
    title: i18n.t("users.table.sub"),
    key: "sub",
    dataIndex: "sub",
    ellipsis: false,
    sorter: true,
    defaultSortOrder: orderAscend
  },
  {
    title: i18n.t("users.table.email"),
    key: "email",
    dataIndex: "email",
    ellipsis: false,
    sorter: true,
    defaultSortOrder: undefined
  },
  {
    title: i18n.t("users.table.profile"),
    key: "profiles",
    dataIndex: "profiles",
    width: "18%",
    ellipsis: false,
    sorter: true,
    defaultSortOrder: undefined,
    render: (record: string[]) => {
      return (
        <>
          {record.map(profile => {
            return (
              <UserProfileTag key={profile} profile={profile}></UserProfileTag>
            );
          })}
        </>
      );
    }
  },
  {
    title: i18n.t("users.table.branch"),
    key: "branch",
    dataIndex: "branch",
    width: "10%",
    render: (branch: string) => {
      return branch ? (
        <Tag>{branchList[branch]?.label || ""}</Tag>
      ) : (
        <Tag>{i18n.t("users.table.all")}</Tag>
      );
    },
    defaultFilteredValue: undefined,
    sorter: true
  },
  {
    title: i18n.t("users.table.newsTheme"),
    key: "newsTheme",
    width: "20%",
    ellipsis: false,
    render: (record: UserInfoResponseDTO) => {
      if (
        record &&
        record.newsThemeIds &&
        record.newsThemeIds.length > 0 &&
        NewsThemeStore.getState().length > 0
      ) {
        const filteredNewsThemes = NewsThemeStore.getState().filter(newsTheme =>
          record.newsThemeIds?.includes(newsTheme.id)
        );
        if (filteredNewsThemes && filteredNewsThemes.length > 0) {
          return (
            <>
              {filteredNewsThemes.map(newsTheme => (
                <Tag key={"newsThemeTag_" + newsTheme.id}>{newsTheme.name}</Tag>
              ))}
            </>
          );
        }
      }
      return <Tag>{i18n.t("users.table.all")}</Tag>;
    },
    defaultSortOrder: undefined,
    sorter: true
  },
  {
    title: i18n.t("users.table.status"),
    key: "status",
    dataIndex: "status",
    width: "5%",
    ellipsis: false,
    render: (status: string) => {
      return <UserStatusTag status={status} />;
    },
    sorter: true,
    defaultSortOrder: undefined
  },
  {
    title: i18n.t("users.table.creationDate"),
    key: "creationDate",
    dataIndex: "creationDate",
    width: "12%",
    ellipsis: false,
    defaultSortOrder: undefined,
    sorter: true,
    render: tooltipDateRender
  },
  // {
  //   title: i18n.t("users.table.lastConnectionDate"),
  //   key: "lastConnectionDate",
  //   dataIndex: "lastConnectionDate",
  //   width: "10%",
  //   ellipsis: false,
  //   sorter: true,
  //   render: tooltipDateRender
  // },
  {
    title: i18n.t("users.table.action"),
    key: "action",
    fixed: alignRight,
    width: "9%",
    onCell: () => {
      return {
        onClick: (e: any) => e.stopPropagation()
      };
    },
    render: (record: UserInfoResponseDTO) => (
      <Popover
        content={popoverContent(record, canDelete)}
        placement="bottomRight"
        trigger="hover"
      >
        <Button
          icon={<MoreOutlined />}
          type="text"
          size="large"
          className="btn-list"
        />
      </Popover>
    )
  }
];
